import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "@/utils/api";

const HARD = ["mobile", "alt_mobile", "email"];
const LABEL = { mobile: "mobile number", alt_mobile: "alternate mobile", email: "email", gst: "GST number", name: "name", company: "company name" };

/** Looks up existing customers (any type) that match what is being typed. Debounced; never throws.
 *  hard   = same mobile / alternate mobile / email  -> cannot be saved again
 *  soft   = same name / company / GST               -> warning, can be saved after the user confirms
 *  fieldMsg = per-field highlight text keyed by mobile | alt_mobile | email | gst | name | company */
export function useDuplicateCheck({ name = "", company = "", mobile = "", altMobile = "", email = "", gst = "", excludeId = "" }) {
  const [matches, setMatches] = useState([]);
  const [hard, setHard] = useState(false);
  const [ack, setAck] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const digits = (v) => (v || "").replace(/\D/g, "");
    const useful = digits(mobile).length >= 10 || digits(altMobile).length >= 10 || (email || "").includes("@")
      || (gst || "").trim().length >= 15 || (name || "").replace(/[^a-z0-9]/gi, "").length >= 4
      || (company || "").replace(/[^a-z0-9]/gi, "").length >= 4;
    if (!useful) { setMatches([]); setHard(false); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/v2/customer-360/duplicate-check", {
          params: { name, company, mobile, alt_mobile: altMobile, email, gst, exclude_id: excludeId },
        });
        if (cancelled) return;
        setMatches(r.data?.matches || []); setHard(!!r.data?.hard); setFailed(false);
      } catch {
        if (!cancelled) setFailed(true); // never block the form just because the check could not run
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [name, company, mobile, altMobile, email, gst, excludeId]);

  // a new set of matches needs a fresh confirmation
  const key = matches.map((m) => m.id + m.reasons.join()).join("|");
  useEffect(() => { setAck(false); }, [key]);

  const fieldMsg = useMemo(() => {
    const out = {};
    matches.forEach((m) => m.reasons.forEach((r) => {
      if (!out[r]) out[r] = `Already exists: ${m.company || m.name}${m.customer_code ? ` (${m.customer_code})` : ""}`;
    }));
    return out;
  }, [matches]);

  const blocked = hard || (matches.length > 0 && !ack);
  return { matches, hard, ack, setAck, fieldMsg, blocked, failed };
}

export default function DuplicateWarning({ dup, linkBase = "/admin/customers", onUse = null, hardMessage = null, hint = null }) {
  const { matches, hard, ack, setAck } = dup;
  if (!matches.length) return null;
  return (
    <div className={`mb-4 rounded-md border p-3 text-sm ${hard ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-900"}`} data-testid="duplicate-warning">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">
            {hard ? (hardMessage || "This customer already exists \u2014 it cannot be created again.") : "A similar customer already exists. Please check before saving."}
          </div>
          <ul className="mt-1 space-y-1">
            {matches.map((m) => (
              <li key={m.id} data-testid="duplicate-match">
                <Link to={`${linkBase}/${m.id}`} target="_blank" className="underline font-semibold">{m.company || m.name}</Link>
                {m.customer_code ? ` \u00b7 ${m.customer_code}` : ""}{m.customer_type ? ` \u00b7 ${m.customer_type}` : ""}
                {m.mobile ? ` \u00b7 ${m.mobile}` : ""}{m.email ? ` \u00b7 ${m.email}` : ""}
                {!m.active ? " \u00b7 inactive" : ""}
                <span className="opacity-80"> — same {m.reasons.map((r) => LABEL[r] || r).join(", ")}</span>
                {onUse && (
                  <button type="button" onClick={() => onUse(m)} data-testid="duplicate-use-btn"
                    className="ml-2 px-2 py-0.5 rounded-sm bg-white border border-current text-xs font-semibold hover:bg-slate-50">
                    Use this customer
                  </button>
                )}
              </li>
            ))}
          </ul>
          {hard ? (
            <p className="mt-2 text-xs">{hint || "Open the existing customer instead. If it is a true duplicate, ask an admin to merge the accounts."}</p>
          ) : (
            <label className="mt-2 flex items-center gap-2 text-xs font-semibold">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="duplicate-ack" />
              I checked — this is a different customer, continue.
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
